import React from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import * as userIntentsService from '../services/userIntentsService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to get global intent counts (total wishlist and favorite counts)
 */
export function useGlobalIntentCounts() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['userIntents', 'counts'],
    queryFn: () => userIntentsService.getGlobalIntentCounts(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get all user intents (wishlist and favorites) with infinite scrolling
 */
export function useUserIntents(limit: number = 20) {
  const { isAuthenticated } = useAuth();

  return useInfiniteQuery({
    queryKey: ['userIntents', 'infinite'],
    queryFn: ({ pageParam }) => userIntentsService.getUserIntents(limit, pageParam),
    getNextPageParam: (lastPage) => lastPage.lastEvaluatedKey,
    enabled: isAuthenticated,
  });
}

/**
 * Hook to get user intents for a specific set
 */
export function useUserIntentsBySet(setId: string) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['userIntents', setId],
    queryFn: () => userIntentsService.getUserIntentsBySet(setId),
    enabled: isAuthenticated && !!setId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to toggle wishlist status
 */
export function useToggleWishlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userIntentsService.toggleWishlist,
    onSuccess: (data) => {
      // Invalidate both global and set-specific queries
      queryClient.invalidateQueries({ queryKey: ['userIntents'] });
      queryClient.invalidateQueries({ queryKey: ['userIntents', data.setId] });
      queryClient.invalidateQueries({ queryKey: ['userIntents', 'counts'] });
      
      // Also invalidate card-specific ownership queries
      // We prioritize the imageId (variant) if available, falling back to cardId
      const targetId = data.imageId || data.cardId;
      queryClient.invalidateQueries({ queryKey: ['onePieceCardOwnership', targetId] });
      queryClient.invalidateQueries({ queryKey: ['pokemonCardOwnership', targetId] });
      
      // Only invalidate the full set list if necessary (this is what causes the big refetches)
      // We can use a more targeted approach or keep it for safety but it will trigger the 304s you see
      queryClient.invalidateQueries({ queryKey: ['onePieceCardOwnerships', data.setId] });
      queryClient.invalidateQueries({ queryKey: ['pokemonCardOwnerships', data.setId] });
    },
  });
}

/**
 * Hook to toggle favorite status
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userIntentsService.toggleFavorite,
    onSuccess: (data) => {
      // Invalidate both global and set-specific queries
      queryClient.invalidateQueries({ queryKey: ['userIntents'] });
      queryClient.invalidateQueries({ queryKey: ['userIntents', data.setId] });
      queryClient.invalidateQueries({ queryKey: ['userIntents', 'counts'] });
      
      // Also invalidate card-specific ownership queries
      const targetId = data.imageId || data.cardId;
      queryClient.invalidateQueries({ queryKey: ['onePieceCardOwnership', targetId] });
      queryClient.invalidateQueries({ queryKey: ['pokemonCardOwnership', targetId] });
      
      queryClient.invalidateQueries({ queryKey: ['onePieceCardOwnerships', data.setId] });
      queryClient.invalidateQueries({ queryKey: ['pokemonCardOwnerships', data.setId] });
    },
  });
}

/**
 * Hook to get user intents for a specific set as a map for easy lookup
 */
export function useUserIntentsMap(setId: string) {
  const { data: intents = [] } = useUserIntentsBySet(setId);

  const intentsMap = React.useMemo(() => {
    return intents.reduce((map, intent) => {
      map[intent.cardId] = intent;
      return map;
    }, {} as Record<string, userIntentsService.UserIntent>);
  }, [intents]);

  return { intentsMap, intents };
}

/**
 * Hook to get ALL user intents as a map
 * Useful for global views like search
 */
export function useAllUserIntentsMap() {
  const { data } = useUserIntents(1000); // Fetch a large enough batch for search views
  
  const intentsMap = React.useMemo(() => {
    if (!data?.pages) return {};
    
    return data.pages.flatMap(page => page.items).reduce((map, intent) => {
      map[intent.cardId] = intent;
      return map;
    }, {} as Record<string, userIntentsService.UserIntent>);
  }, [data]);

  return { intentsMap, isLoading: !data };
}
